/** Attention (DESIGN §24.2): what in the week a coach should look at first, from what the portal has read. */
import { addDays, mondayOf, parseDay, weekdayShort } from "./days.js";
import { type LoadData, type LoadStatus, loadStatus, trendText } from "./load.js";
import { plural, working } from "./plan.js";
import { metresText, sessionMetres, metresTotal } from "./running.js";
import { bodyAreasText, bodyOf, effortOf } from "./effort.js";
import type { BodyReport, Workout } from "./types.js";
import type { Athlete, WeekModel } from "./week.js";

export type FlagKind = "pain" | "injury" | "illness" | "sleep";

/** One mention in an athlete's shared notes, as the Worker found it; `quote` is their own words. */
export interface AttentionFlag {
  id: string;
  athleteID: string;
  workoutID: string;
  date: string;
  kind: FlagKind;
  area?: string | null;
  quote: string;
}

/** One of an athlete's sessions this week, as `upcoming` reads it. */
export interface ComingSession {
  date: string;
  name: string;
  done: boolean;
  /** A weights session's exercises: shown by name when one is what loads the area. */
  exercises?: string[];
  /** Another part's lines: matched, never shown. */
  lines?: string[];
}

export interface Coming {
  date: string;
  name: string;
}

/**
 * Body parts, and the words of sessions that load them. An area matches a part when it says one
 * of its names; a session matches when its name, an exercise or a line says one of its words.
 */
export const BODY_PARTS: { names: string[]; words: string[] }[] = [
  {
    names: ["hamstring", "hammy", "hammies", "ham"],
    words: ["sprint", "max velocity", "maxv", "flying", "fly", "rdl", "romanian", "nordic", "hurdle", "deadlift", "good morning", "leg curl", "accel", "acceleration", "block", "bound"],
  },
  {
    names: ["quad", "quadricep", "quadriceps", "thigh", "knee", "patella", "patellar"],
    words: ["squat", "lunge", "split squat", "step up", "step-up", "jump", "bound", "plyo", "plyometric", "leg extension", "leg press", "hurdle", "clean", "depth"],
  },
  {
    names: ["calf", "calves", "achilles", "ankle", "shin", "foot", "feet", "heel"],
    words: ["sprint", "accel", "acceleration", "max velocity", "plyo", "plyometric", "jump", "hop", "pogo", "bound", "skip", "calf raise", "hill", "hurdle"],
  },
  {
    names: ["groin", "adductor", "hip", "hip flexor", "hips"],
    words: ["sprint", "max velocity", "hurdle", "lunge", "copenhagen", "squat", "bound", "skip", "accel"],
  },
  {
    names: ["glute", "glutes", "buttock"],
    words: ["sprint", "hip thrust", "squat", "deadlift", "rdl", "lunge", "bound", "sled"],
  },
  {
    names: ["back", "lower back", "spine", "lumbar"],
    words: ["deadlift", "rdl", "squat", "clean", "snatch", "good morning", "row", "sled", "resisted"],
  },
  {
    names: ["upper back", "shoulder blade", "shoulder blades", "trap", "traps", "rhomboid", "rhomboids", "lat", "lats"],
    words: ["row", "pull up", "pull-up", "chin up", "chin-up", "press", "bench", "snatch", "clean", "shrug", "deadlift"],
  },
  {
    names: ["shoulder", "shoulders", "rotator"],
    words: ["press", "bench", "overhead", "snatch", "jerk", "pull up", "pull-up", "chin up", "chin-up", "dip", "push up", "push-up", "throw"],
  },
  {
    names: ["elbow", "wrist", "hand", "forearm"],
    words: ["clean", "snatch", "press", "bench", "curl", "pull up", "pull-up", "chin up", "chin-up", "front squat"],
  },
  {
    names: ["neck"],
    words: ["shrug", "clean", "snatch"],
  },
];

function escape(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Whole words (or a phrase), with or without a plural s: "rdl" finds "RDLs", not "hurdle". */
function says(text: string, word: string): boolean {
  return new RegExp("(^|[^\\p{L}\\p{N}])" + escape(word) + "(e?s)?(?![\\p{L}\\p{N}])", "iu").test(text);
}

/** The session words for an area the athlete named; none when it names no part in the table. */
export function wordsFor(area: string | null | undefined): string[] {
  const text = String(area ?? "").trim();
  if (!text) return [];
  const words: string[] = [];
  for (const part of BODY_PARTS) if (part.names.some((n) => says(text, n))) for (const w of part.words) if (!words.includes(w)) words.push(w);
  return words;
}

/** The athlete's sessions still to come this week that load the area the flag names. */
export function upcoming(flag: Pick<AttentionFlag, "area">, sessions: ComingSession[], today: string): Coming[] {
  const words = wordsFor(flag.area);
  if (!words.length) return [];
  const hits = (text: string) => words.some((w) => says(text, w));
  const out: Coming[] = [];
  for (const s of [...sessions].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))) {
    if (s.date < today || (s.date === today && s.done)) continue;
    let name: string | null = null;
    if (hits(s.name)) name = s.name;
    else {
      const lifts = (s.exercises ?? []).filter(hits);
      if (lifts.length) name = lifts.join(", ");
      else if ((s.lines ?? []).some(hits)) name = s.name;
    }
    if (name && !out.some((c) => c.date === s.date && c.name === name)) out.push({ date: s.date, name });
  }
  return out;
}

/** An athlete's weights sessions and the rest of their plan this week, for `upcoming`. */
export function athleteSessions(a: Pick<Athlete, "given" | "track">): ComingSession[] {
  const out: ComingSession[] = [];
  for (const g of a.given) {
    const exercises: string[] = [];
    for (const b of working(g.row.plan?.blocks)) for (const e of b.exercises ?? []) if (e.name && !exercises.includes(e.name)) exercises.push(e.name);
    out.push({ date: g.date, name: g.title, done: g.done.length > 0, exercises });
  }
  for (const t of a.track) out.push({ date: t.date, name: t.label, done: t.done.length > 0, lines: t.lines });
  return out;
}

export type ExceptionKind = "load" | "notLogged" | "skipped" | "records" | "running" | "quiet" | "maxEffort";

/** A sore or hurt report from one of the week's workouts (§27.2); `where` in lower case, as a line reads on. */
export interface BodyFlag {
  date: string;
  level: BodyReport["level"];
  where: string;
  note: string | null;
}

export type AttentionItem =
  | { key: string; type: "flag"; athleteID: string; name: string; flag: AttentionFlag; coming: Coming[] }
  | { key: string; type: "body"; athleteID: string; name: string; body: BodyFlag; coming: Coming[] }
  | { key: string; type: "exception"; athleteID: string; name: string; kind: ExceptionKind; text: string; distance: number };

export interface AttentionInput {
  model: Pick<WeekModel, "monday" | "sunday" | "today" | "athletes" | "workouts">;
  flags?: AttentionFlag[] | null;
  load?: LoadData | null;
  /** Everyone's workouts the week before, for running; null when it couldn't be read. */
  previous?: Pick<Workout, "athleteID" | "date" | "session">[] | null;
}

const RUNNING_OFF = 0.3;
const QUIET_DAYS = 7;
const MAX_EFFORT = 9;
const MAX_ROWS = 5;

function daysBetween(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86_400_000);
}

/** The week's verdict as the matrix reads it (§16.5): the last full week while this one runs. */
function loadFor(load: LoadData | null | undefined, id: string, week: string, today: string): { status: LoadStatus; week: string } | null {
  const athlete = load?.athletes.find((a) => a.id === id);
  if (!load || !athlete) return null;
  let index = load.weeks.indexOf(week);
  if (index < 0) return null;
  if (load.weeks[index] === mondayOf(today) && index > 0) index -= 1;
  return { status: loadStatus(load.weeks, athlete, index), week: load.weeks[index]! };
}

function metres(workouts: Pick<Workout, "athleteID" | "session">[], id: string): number {
  return workouts.filter((w) => w.athleteID === id).reduce((n, w) => n + metresTotal(sessionMetres(w.session)), 0);
}

/**
 * Everything that qualifies: hurt reports, note flags, sore reports (each newest first), then
 * exceptions by how far from usual. Distance is a fraction off usual: load and running their
 * change; sessions not logged the share of those owed; quiet and a session at 9 or 10 a full 1;
 * personal bests 0, since a record is not off anything.
 */
export function attentionItems({ model, flags, load, previous }: AttentionInput): AttentionItem[] {
  const byID = new Map(model.athletes.map((a) => [a.id, a]));
  const flagItems: AttentionItem[] = [];
  for (const f of flags ?? []) {
    const a = byID.get(f.athleteID);
    if (!a || !f.quote?.trim() || f.date < model.monday || f.date > model.sunday) continue;
    flagItems.push({ key: "flag:" + f.id, type: "flag", athleteID: a.id, name: a.name, flag: f, coming: upcoming(f, athleteSessions(a), model.today) });
  }
  const hurt: AttentionItem[] = [];
  const sore: AttentionItem[] = [];
  for (const w of model.workouts) {
    const a = byID.get(w.athleteID);
    const report = bodyOf(w.session);
    if (!a || !report || w.date < model.monday || w.date > model.sunday) continue;
    const where = bodyAreasText(report, true);
    const body: BodyFlag = { date: w.date, level: report.level, where, note: report.note ?? null };
    const key = "body:" + a.id + ":" + (w.session.id ?? w.date);
    (report.level === "hurt" ? hurt : sore).push({ key, type: "body", athleteID: a.id, name: a.name, body, coming: upcoming({ area: where }, athleteSessions(a), model.today) });
  }
  const dateOf = (i: AttentionItem) => (i.type === "flag" ? i.flag.date : i.type === "body" ? i.body.date : "");
  const newest = (x: AttentionItem, y: AttentionItem) => {
    const a = dateOf(x);
    const b = dateOf(y);
    return a !== b ? (a > b ? -1 : 1) : x.name.localeCompare(y.name);
  };
  flagItems.sort(newest);
  hurt.sort(newest);
  sore.sort(newest);

  const week = model.monday;
  const current = mondayOf(model.today) === week;
  const ongoing = model.today <= model.sunday;
  const out: Extract<AttentionItem, { type: "exception" }>[] = [];
  const add = (a: Athlete, kind: ExceptionKind, text: string, distance: number) =>
    out.push({ key: kind + ":" + a.id + ":" + week, type: "exception", athleteID: a.id, name: a.name, kind, text, distance });

  for (const a of model.athletes) {
    const l = loadFor(load, a.id, week, model.today);
    if (l && (l.status.trend === "high" || l.status.trend === "low")) {
      const verdict = trendText(l.status);
      const when = l.week === week ? "this week" : "last week";
      add(a, "load", "Load " + verdict.charAt(0).toLowerCase() + verdict.slice(1) + " " + when, Math.abs(l.status.change ?? 100) / 100);
    }

    if (a.missed >= 2) add(a, "notLogged", plural(a.missed, "session", "sessions") + " not logged this week", a.owed ? a.missed / a.owed : 1);

    if (a.skipped >= 2) add(a, "skipped", "Skipped the whole session " + a.skipped + " times this week", a.owed ? a.skipped / a.owed : 1);

    if (a.prs > 0) add(a, "records", plural(a.prs, "personal best", "personal bests") + " this week", 0);

    if (previous) {
      const before = metres(previous, a.id);
      const now = metres(model.workouts, a.id);
      if (before > 0) {
        const change = now / before - 1;
        // A week still running only grows, so it is never "below" until it is over.
        const off = change >= RUNNING_OFF || (!ongoing && change <= -RUNNING_OFF);
        if (off) {
          const pct = Math.round(Math.abs(change) * 100);
          add(a, "running", `Running ${metresText(now)}, ${pct}% ${change > 0 ? "above" : "below"} the week before (${metresText(before)})`, Math.abs(change));
        }
      }
    }

    for (const w of model.workouts) {
      const e = effortOf(w.session);
      if (w.athleteID !== a.id || e == null || e < MAX_EFFORT || w.date < model.monday || w.date > model.sunday) continue;
      const title = (w.session.title ?? "").trim();
      out.push({
        key: "maxEffort:" + a.id + ":" + (w.session.id ?? w.date),
        type: "exception",
        athleteID: a.id,
        name: a.name,
        kind: "maxEffort",
        text: "Max effort on " + weekdayShort(w.date) + (title ? " · " + title : ""),
        distance: 1,
      });
    }

    if (current && a.lastWorkout) {
      const days = daysBetween(a.lastWorkout, model.today);
      if (days >= QUIET_DAYS) add(a, "quiet", `Nothing logged for ${days} days`, 1);
    }
  }
  out.sort((x, y) => y.distance - x.distance || x.name.localeCompare(y.name));
  return [...hurt, ...flagItems, ...sore, ...out];
}

/** What the section shows: the first five not dismissed in this browser. */
export function attentionRows(items: AttentionItem[], dismissed: ReadonlySet<string>, max = MAX_ROWS): AttentionItem[] {
  return items.filter((i) => !dismissed.has(i.key)).slice(0, max);
}

/** The week before `monday`. */
export function previousMonday(monday: string): string {
  return addDays(monday, -7);
}
